import { GridApiPro, GridValidRowModel } from '@mui/x-data-grid-pro';
import { GridStateColDef } from '@mui/x-data-grid-pro/internals';
import {
  ApiError,
  Assessment,
  AssessmentEntry,
  AssessmentEntryComment,
  AssessmentGroup,
  AssessmentMethod,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
  GetMarkbookSort,
  GroupMember,
  GroupSubject,
  MarkbookAssessmentEntry,
  MarkbookCategory,
  MarkbookStudent,
  MarkbookStudents,
  SubjectAverageScore,
} from '@schooly/api';
import { ColumnDef } from '@schooly/components/data-grid';

import { HandleChangeSortParams } from '../MarkbookPreview/useAssessmentsMarkbookForGroups';
import { MarkbookColumnArrangeBy } from '../MarkbookSettings/MarkbookSettings';
import { MarkbookForGroupStyling, STUDENT_COL_FIELD_KEY } from './assessmentStyling';
import { AverageColorProps, CellColorProps } from './colouredCells';
import { MarkbookForGroupsStudentsHeaderCellProps } from './MarkbookCell';

export type MarkbookForGroupRow = GridValidRowModel & {
  id: string;
  student: GroupMember;
  assessmentEntries?: AssessmentWithEntries;
};

export type RenderMarkbookForGroupMethodProps = {
  student: GroupMember;
  method: AssessmentMethod;
  assessmentEntries: AssessmentWithEntries;
  scoreControllerProps?: Record<string, unknown>;
  gradeControllerProps?: Record<string, unknown>;
  commentControllerProps?: Record<string, unknown>;
  cellId?: string;
};

export type MarkbookForGroupEventHandlers = {
  onScoreChange: (studentId: string, methodId: string, newScore: number) => Promise<void>;
  onGradeChange: (studentId: string, methodId: string, newGrade: string) => Promise<void>;
  onCommentChange: (studentId: string, methodId: string, newComment: string) => Promise<void>;
  onColumnMouseOver?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseLeave?: () => void;
  renderAssessmentEntryMethod?: (props: RenderMarkbookForGroupMethodProps) => React.ReactNode;
};

export type MarkbookForGroupProps = {
  groupId: string;
  assessments: AssessmentWithEntries[];
  students: GroupMember[];
  lists?: Record<string, AssessmentsGrade>;
  dateFrom?: string;
  dateTo?: string;
  onSuccess?: () => void;
  showHeader?: boolean;
  styling: MarkbookForGroupStyling;
  showPercentage?: boolean;
  getAssessmentEntryColor?: (cellProps: CellColorProps) => string | null;
  onApiRefReady?: (apiRef: React.MutableRefObject<GridApiPro>) => void;
  onUpdateError?: (error: ApiError) => void;
};

export type UpdateAssessmentEntryParams = {
  studentId: string;
  assessmentId: string;
  methodId: string;
  entry: AssessmentEntry | null;
  comments?: AssessmentEntryComment[];
  groupId: string;
  methodType: AssessmentMethodType;
};

export type UpdateMarkbookForGroupsAssessmentEntryParams = UpdateAssessmentEntryParams & {
  categoryId: string;
};

export type AssessmentCellState = {
  status: 'loading' | 'success' | 'error' | null;
};

export type RenderMarkbookForGroupsMethodProps = {
  student: MarkbookStudent;
  method: AssessmentMethod;
  assessmentEntries: MarkbookAssessmentEntry[];
  assessmentId: string;
  scoreControllerProps?: Record<string, unknown>;
  gradeControllerProps?: Record<string, unknown>;
  commentControllerProps?: Record<string, unknown>;
  cellId?: string;
  categoryId: string;
};

export type MarkbookStudentWithEntries = MarkbookStudents['results'][0];

export type MarkbookCategoryWithStudents = {
  category: MarkbookCategory;
  students: MarkbookStudentWithEntries[];
  totalStudents: number;
};

export type MarkbookBaselineAssessmentConfig = {
  kind: 'assessment';
  assessmentId: string;
  method: AssessmentMethod;
  subject: MarkbookSubject;
};

export type MarkbookBaselineAverageConfig = {
  kind: 'average';
  subject: MarkbookSubject;
};

export type MarkbookBaselineConfig =
  | MarkbookBaselineAssessmentConfig
  | MarkbookBaselineAverageConfig;

export type MarkbookBaselineAssessment = {
  kind: 'assessment';
  assessment: Assessment;
} & Pick<MarkbookBaselineAssessmentConfig, 'subject' | 'method'>;

export type MarkbookBaselineAverage = {
  kind: 'average';
} & Pick<MarkbookBaselineAverageConfig, 'subject'>;

export type MarkbookBaseline = MarkbookBaselineAssessment | MarkbookBaselineAverage;

export type MarkbookForGroupsProps = {
  assessments: Assessment[];
  categories: MarkbookCategoryWithStudents[];
  hasNextPage: (id: string) => boolean;
  fetchNextPage: (id: string) => void;
  refetch: (id: string) => void;
  isFetchingNextPage: (id: string) => boolean;
  lists?: Record<string, AssessmentsGrade>;
  studentsHeaderCellProps: MarkbookForGroupsStudentsHeaderCellProps;
  onChangeSort: (params: HandleChangeSortParams) => void;
  isSorting: boolean;
  sort?: GetMarkbookSort;
  showPercentage?: boolean;
  showStudentsAverage?: boolean;
  columnArrangeBy: MarkbookColumnArrangeBy;
  baseline?: MarkbookBaseline;
  getAssessmentEntryColor?: (cellProps: CellColorProps) => string | null;
  getAverageCellColor?: (cellProps: AverageColorProps) => string | null;
  onSuccessfulEntryUpdate?: () => void;
  onApiRefReady?: (apiRef: React.MutableRefObject<GridApiPro>) => void;
  onUpdateError?: (error: ApiError) => void;
  onCellClickOverride?: (e: React.MouseEvent) => void;
  onSetBaselineConfig?: (v: MarkbookBaselineConfig | null) => void;
};

export enum MarkbookForGroupsRowType {
  Student = 'student',
  Category = 'category',
  LoadMore = 'loadMore',
  LoadingNextPage = 'loadingNextPage',
}

type MarkbookForGroupsRowBase = GridValidRowModel & {
  id: string;
  type: MarkbookForGroupsRowType;
};

export type MarkbookAssessmentEntriesMap = Record<
  Assessment['id'],
  Record<
    AssessmentGroup['id'],
    Record<MarkbookAssessmentEntry['method']['id'], MarkbookAssessmentEntry[]>
  >
>;

export type MarkbookForGroupsStudentRow = MarkbookForGroupsRowBase & {
  type: MarkbookForGroupsRowType.Student;
  data: {
    student: MarkbookStudent;
    assessmentEntries: MarkbookAssessmentEntriesMap;
    categoryId: string;
    average: SubjectAverageScore[];
  };
};

export type MarkbookForGroupsCategoryRow = MarkbookForGroupsRowBase & {
  type: MarkbookForGroupsRowType.Category;
  data: {
    category: MarkbookCategory;
    totalStudents: number;
    rowsCount: number;
  };
};

export type MarkbookForGroupsLoadMoreRow = MarkbookForGroupsRowBase & {
  type: MarkbookForGroupsRowType.LoadMore;
  data: { categoryId: string };
};

export type MarkbookForGroupsLoadingNextPageRow = MarkbookForGroupsRowBase & {
  type: MarkbookForGroupsRowType.LoadingNextPage;
  data: { categoryId: string };
};

export type MarkbookForGroupsRow =
  | MarkbookForGroupsStudentRow
  | MarkbookForGroupsCategoryRow
  | MarkbookForGroupsLoadMoreRow
  | MarkbookForGroupsLoadingNextPageRow;

export const isMarkbookForGroupsStudentRow = (
  row: MarkbookForGroupsRow,
): row is MarkbookForGroupsStudentRow => {
  return row.type === MarkbookForGroupsRowType.Student;
};

export const isMarkbookCategoryRow = (
  row: MarkbookForGroupsRow,
): row is MarkbookForGroupsCategoryRow => {
  return row.type === MarkbookForGroupsRowType.Category;
};

export const isStudentsColumn = (field: string): boolean => {
  return field === STUDENT_COL_FIELD_KEY;
};

export enum MarkbookColumnType {
  Students = 'students',
  Assessment = 'assessment',
  Average = 'average',
  Baseline = 'baseline',
}

export type MarkbookSubject = {
  groups: { id: string; name: string }[];
  labelId?: string;
  name?: string;
} & Pick<GroupSubject, 'id'>;

export type MarkbookForGroupsColumnData = {
  assessmentId: string;
  method: AssessmentMethod;
  subject: MarkbookSubject;
};
export type MarkbookForGroupsAverageColumnData = {
  subject: MarkbookSubject;
};

export type MarkbookForGroupsColumn =
  | ColumnDef<MarkbookForGroupsRow, AssessmentCellState, MarkbookForGroupsColumnData | undefined>
  | ColumnDef<MarkbookForGroupsRow, AssessmentCellState, MarkbookForGroupsAverageColumnData>;

export const isMarkbookForGroupsColumnData = (
  colDef: GridStateColDef,
): colDef is GridStateColDef & { data: MarkbookForGroupsColumnData } => {
  if (!('data' in colDef)) return false;
  const data = colDef.data;

  if (typeof data !== 'object' || data === null) return false;

  const hasAssessmentId = 'assessmentId' in data && typeof data.assessmentId === 'string';
  const hasSubject = 'subject' in data && typeof data.subject === 'object' && data.subject !== null;
  const hasMethod = 'method' in data && typeof data.method === 'object' && data.method !== null;

  if (!hasAssessmentId || !hasSubject || !hasMethod) return false;

  return true;
};
